import { Injectable } from '@angular/core';

import {
  DprFormulaContext,
  DprFormulaValues,
  DprStatusTrackerFormulaContext,
  DprTableOfColumnsFormulaContext,
  FormulaCell,
  FormulaHistoryItem,
  StatusTrackerHistoryItem,
  TableOfColumnHistoryItem,
} from '@dpr-details/models';
import { DprStatusTrackerGridData } from '@dpr-details/models/grid';
import {
  ArithmeticFormulaExpressionEvaluator,
  FormulaDisplayPrecision,
  getFormulaInputColumnIds,
  getStatusTrackerFormulas,
  getTableOfColumnFormulas,
} from '@dpr-details/utils';
import {
  DprStatusTrackerDetails,
  DprTableOfColumnDetail,
} from '@shared/models/dpr';
import {
  StatusTrackerWithData,
  TableOfColumnWithData,
} from '@shared/models/project-setup';
import { FormulaExpressionCalculationService } from './formula-expression-calculation.service';
import { StatusTrackerFormulaExpressionCalculationService } from './status-tracker-formula-expression-calculation.service';
import { TableOfColumnFormulaExpressionCalculationService } from './table-of-column-formula-expression-calculation.service';

/**
 * Feeds the formula engine from DPR data. `DprCarryOverFormulaCalculationService`, the
 * grid build and a live edit all go through here, so the three cannot work a
 * value out differently.
 */
@Injectable()
export class DprFormulaCalculationService {
  private readonly evaluator = new ArithmeticFormulaExpressionEvaluator();

  /** One context per table of columns that has formulas, keyed by table id. */
  createTableOfColumnsContexts(
    tableOfColumns: TableOfColumnWithData[],
    dprDate: Date | string,
    history: TableOfColumnHistoryItem[]
  ): Map<number, DprTableOfColumnsFormulaContext> {
    const contexts = new Map<number, DprTableOfColumnsFormulaContext>();

    tableOfColumns?.forEach((tableOfColumn) => {
      const context = this.createTableOfColumnsContext(
        tableOfColumn,
        dprDate,
        history
      );

      if (context) {
        contexts.set(context.tableOfColumnId, context);
      }
    });

    return contexts;
  }

  /** One context per status tracker that has formulas, keyed by status tracker id. */
  createStatusTrackerContexts(
    statusTrackers: StatusTrackerWithData[],
    dprDate: Date | string,
    history: StatusTrackerHistoryItem[]
  ): Map<number, DprStatusTrackerFormulaContext> {
    const contexts = new Map<number, DprStatusTrackerFormulaContext>();

    statusTrackers?.forEach((statusTracker) => {
      const context = this.createStatusTrackerContext(
        statusTracker,
        dprDate,
        history
      );

      if (context) {
        contexts.set(context.statusTrackerId, context);
      }
    });

    return contexts;
  }

  /** Writes the formula values straight onto the grid rows, as the grid is built. */
  applyTableOfColumnsFormulas(
    context: DprTableOfColumnsFormulaContext,
    rows: DprTableOfColumnDetail[]
  ): void {
    const values = this.calculateTableOfColumnsRows(context, rows);

    rows.forEach((row) =>
      values.get(String(row.id))?.forEach((value, columnId) => {
        row[columnId] = value;
      })
    );
  }

  /** Grid rows, keyed by item id. */
  calculateTableOfColumnsRows(
    context: DprTableOfColumnsFormulaContext,
    rows: DprTableOfColumnDetail[]
  ): DprFormulaValues {
    const cells: FormulaCell[] = rows.flatMap((row) =>
      context.cellColumnIds.map((columnId) => ({
        rowId: row.id,
        columnId,
        value: row[columnId] ?? null,
      }))
    );

    return this.calculateTableOfColumns(
      context,
      cells,
      rows.map((row) => row.id)
    );
  }

  /**
   * Rows as stored, with their cells in `dprTableOfColumnDetailItems`, each value
   * rounded to `precision` decimals.
   */
  calculateTableOfColumnsDetails(
    context: DprTableOfColumnsFormulaContext,
    details: DprTableOfColumnDetail[],
    precision = FormulaDisplayPrecision
  ): DprFormulaValues {
    const columnIds = new Set(context.cellColumnIds);
    const cells: FormulaCell[] = details.flatMap((detail) =>
      (detail.dprTableOfColumnDetailItems ?? [])
        .filter((item) => columnIds.has(item.tableOfColumnItemId))
        .map((item) => ({
          rowId: detail.id,
          columnId: item.tableOfColumnItemId,
          value: item.value,
        }))
    );

    return this.calculateTableOfColumns(
      context,
      cells,
      details.map((detail) => detail.id),
      precision
    );
  }

  /** Writes the formula values straight onto the grid rows, as the grid is built. */
  applyStatusTrackerFormulas(
    context: DprStatusTrackerFormulaContext,
    rows: DprStatusTrackerGridData[]
  ): void {
    const values = this.calculateStatusTrackerRows(context, rows);

    rows.forEach((row) =>
      values.get(String(row.rowId))?.forEach((value, columnId) => {
        row[context.columnNamesById.get(columnId)] = value;
      })
    );
  }

  /** Grid rows, keyed by column name. */
  calculateStatusTrackerRows(
    context: DprStatusTrackerFormulaContext,
    rows: DprStatusTrackerGridData[]
  ): DprFormulaValues {
    const cells: FormulaCell[] = rows.flatMap((row) =>
      context.cellColumnIds.map((columnId) => ({
        rowId: row.rowId,
        columnId,
        value: row[context.columnNamesById.get(columnId)] ?? null,
      }))
    );

    return this.calculateStatusTracker(
      context,
      cells,
      rows.map((row) => row.rowId)
    );
  }

  /**
   * Cells as stored, one `DprStatusTrackerDetails` entry per cell, each value
   * rounded to `precision` decimals.
   */
  calculateStatusTrackerDetails(
    context: DprStatusTrackerFormulaContext,
    details: DprStatusTrackerDetails[],
    precision = FormulaDisplayPrecision
  ): DprFormulaValues {
    const columnIds = new Set(context.cellColumnIds);
    const cells: FormulaCell[] = details
      .filter(
        (detail) =>
          detail.statusTrackerId === context.statusTrackerId &&
          columnIds.has(detail.columnId)
      )
      .map((detail) => ({
        rowId: detail.rowId,
        columnId: detail.columnId,
        value: detail.value,
      }));

    return this.calculateStatusTracker(
      context,
      cells,
      context.templateRowIds,
      precision
    );
  }

  //#region Private methods

  private createTableOfColumnsContext(
    tableOfColumn: TableOfColumnWithData,
    dprDate: Date | string,
    history: TableOfColumnHistoryItem[]
  ): DprTableOfColumnsFormulaContext | null {
    const formulas = getTableOfColumnFormulas(tableOfColumn);

    if (!formulas.length) {
      return null;
    }

    const anchorColumnId =
      tableOfColumn.anchorColumn?.anchorColumnId == null
        ? null
        : +tableOfColumn.anchorColumn.anchorColumnId;

    return {
      tableOfColumnId: +tableOfColumn.id,
      anchorColumnId,
      dprDate,
      formulas,
      cellColumnIds: [
        ...getFormulaInputColumnIds(formulas),
        ...(anchorColumnId == null ? [] : [anchorColumnId]),
      ],
      history,
    };
  }

  private createStatusTrackerContext(
    statusTracker: StatusTrackerWithData,
    dprDate: Date | string,
    history: StatusTrackerHistoryItem[]
  ): DprStatusTrackerFormulaContext | null {
    const formulas = getStatusTrackerFormulas(statusTracker);

    if (!formulas.length) {
      return null;
    }

    return {
      statusTrackerId: +statusTracker.id,
      dprDate,
      formulas,
      cellColumnIds: [...getFormulaInputColumnIds(formulas)],
      templateRowIds: (statusTracker.rows ?? []).map((row) => row.id),
      columnNamesById: new Map(
        (statusTracker.columns ?? []).map((column) => [+column.id, column.name])
      ),
      history,
    };
  }

  private calculateTableOfColumns(
    context: DprTableOfColumnsFormulaContext,
    cells: FormulaCell[],
    rowIds: (number | string)[],
    precision = FormulaDisplayPrecision
  ): DprFormulaValues {
    return this.calculate(
      new TableOfColumnFormulaExpressionCalculationService(
        this.evaluator,
        context.formulas,
        cells,
        context.tableOfColumnId,
        context.anchorColumnId
      ),
      context,
      rowIds,
      precision
    );
  }

  private calculateStatusTracker(
    context: DprStatusTrackerFormulaContext,
    cells: FormulaCell[],
    rowIds: (number | string)[],
    precision = FormulaDisplayPrecision
  ): DprFormulaValues {
    return this.calculate(
      new StatusTrackerFormulaExpressionCalculationService(
        this.evaluator,
        context.formulas,
        cells,
        context.statusTrackerId,
        rowIds
      ),
      context,
      rowIds,
      precision
    );
  }

  /**
   * Every row gets a value for every formula column: the engine leaves out a row
   * with nothing to contribute, and that row shows an empty cell.
   */
  private calculate<THistory extends FormulaHistoryItem>(
    engine: FormulaExpressionCalculationService<THistory>,
    context: DprFormulaContext & { history: THistory[] },
    rowIds: (number | string)[],
    precision: number
  ): DprFormulaValues {
    const values: DprFormulaValues = new Map();

    rowIds.forEach((rowId) =>
      values.set(
        String(rowId),
        new Map<number, string | null>(
          context.formulas.map((formula) => [formula.columnId, null])
        )
      )
    );

    context.formulas.forEach((formula) =>
      engine
        .calculateFormula(
          formula.columnId,
          context.history,
          context.dprDate,
          precision
        )
        ?.forEach(({ rowId, cellValue }) =>
          values.get(String(rowId))?.set(formula.columnId, cellValue)
        )
    );

    return values;
  }

  //#endregion
}
